/* CALM presentation adapter. Shrine retains ownership of prices, quantity and submissions. */
(() => {
  if (window.calmPdpRebuildInstalled) return;
  window.calmPdpRebuildInstalled = true;
  const controllers = new Map();
  const setText = (node, value) => { if (node && node.textContent !== value) node.textContent = value; };

  class CalmPdp {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.section;
      this.abort = new AbortController();
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(root, {subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'checked', 'class']});
      document.addEventListener('change', () => this.schedule(), {signal: this.abort.signal});
      document.addEventListener('click', event => this.onClick(event), {signal: this.abort.signal});
      this.cart = document.querySelector('cart-drawer');
      if (this.cart) {
        this.cartObserver = new MutationObserver(() => this.schedule());
        this.cartObserver.observe(this.cart, {subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['class', 'disabled']});
      }
      this.schedule();
    }

    get quantities() { return this.root.querySelector('quantity-breaks'); }
    get submit() { return document.getElementById(`ProductSubmitButton-${this.sectionId}`); }
    get inputs() { return Array.from(this.quantities?.querySelectorAll('input[type="radio"][name="quantity"]') || []); }
    get variant() { return this.root.querySelector('product-form [name="id"]')?.value; }
    get guarantee() { return this.root.querySelector('.icon-with-text .h4')?.textContent.trim() || ''; }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (!this.root.isConnected) return;
        this.sync();
      });
    }

    labelFor(input) {
      return Array.from(this.quantities?.querySelectorAll('label.quantity-break') || []).find(label => label.htmlFor === input.id);
    }

    sync() {
      const inputs = this.inputs;
      const selected = inputs.find(input => input.checked);
      const submit = this.submit;
      const busy = !submit || submit.disabled || submit.getAttribute('aria-disabled') === 'true';
      const quantityLabel = (this.root.dataset.calmQuantityLabel || '').replace('[quantity]', selected?.value || '');
      this.root.querySelectorAll('[data-calm-selected-quantity]').forEach(node => setText(node, quantityLabel));

      document.querySelectorAll('[data-calm-repeat-offer]').forEach(section => {
        const controls = section.querySelector('[data-calm-repeat-controls]');
        const fallback = section.querySelector('[data-calm-repeat-fallback]');
        fallback.href = `#MainProduct-${this.sectionId}`;
        if (!selected || !submit) { controls.hidden = true; fallback.hidden = false; return; }
        controls.hidden = false;
        fallback.hidden = true;
        const container = section.querySelector('[data-calm-repeat-options]');
        const signature = inputs.map(input => input.id).join('|');
        if (container.dataset.calmSignature !== signature) {
          container.replaceChildren();
          inputs.forEach(input => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'calm-repeat-offer__option';
            button.dataset.calmSourceInput = input.id;
            container.append(button);
          });
          container.dataset.calmSignature = signature;
        }
        inputs.forEach((input, index) => {
          const button = container.children[index];
          const source = this.labelFor(input)?.querySelector('.quantity-break__content');
          if (source && button.dataset.calmMarkup !== source.innerHTML) {
            const content = source.cloneNode(true);
            content.querySelectorAll('[id], [data-shopify-editor-block]').forEach(node => {
              node.removeAttribute('id');
              node.removeAttribute('data-shopify-editor-block');
            });
            content.querySelectorAll('.variant-money-change, .variant-price-update').forEach(node => node.classList.remove('variant-money-change', 'variant-price-update'));
            button.replaceChildren(content);
            button.dataset.calmMarkup = source.innerHTML;
          }
          button.setAttribute('aria-pressed', String(input.checked));
          button.disabled = busy || input.disabled;
        });
        const repeatSubmit = section.querySelector('[data-calm-repeat-submit]');
        if (repeatSubmit.disabled !== busy) repeatSubmit.disabled = busy;
        repeatSubmit.setAttribute('aria-busy', String(submit.classList.contains('loading')));
        const sourceError = this.root.querySelector('.product-form__error-message-wrapper');
        const error = sourceError && !sourceError.hidden ? sourceError.querySelector('.product-form__error-message')?.textContent.trim() : '';
        const repeatError = section.querySelector('[data-calm-repeat-error]');
        setText(repeatError, error || '');
        if (repeatError) repeatError.hidden = !error;
      });
      this.syncCart();
    }

    onClick(event) {
      const option = event.target.closest('[data-calm-source-input]');
      if (option) {
        const input = this.inputs.find(item => item.id === option.dataset.calmSourceInput);
        if (input && !input.disabled) input.click();
        this.schedule();
        return;
      }
      if (event.target.closest('[data-calm-repeat-submit]')) {
        const submit = this.submit;
        if (submit && !submit.disabled && submit.getAttribute('aria-disabled') !== 'true') submit.click();
        this.schedule();
        return;
      }
      const upgrade = event.target.closest('[data-calm-upgrade]');
      if (upgrade) {
        const quantity = document.getElementById(upgrade.dataset.calmUpgrade);
        if (!quantity || Number(quantity.value) !== 1 || quantity.disabled) return;
        upgrade.disabled = true;
        quantity.value = '2';
        quantity.dispatchEvent(new Event('change', {bubbles: true}));
      }
    }

    syncCart() {
      const drawer = this.cart?.querySelector('#CartDrawer');
      if (!drawer) return;
      const rows = Array.from(drawer.querySelectorAll('.cart-item'));
      const calmRows = rows.filter(row => {
        const link = row.querySelector('.cart-item__name');
        if (!link) return false;
        const url = new URL(link.href, window.location.origin);
        return url.pathname.endsWith(`/products/${this.root.dataset.calmHandle}`);
      });
      if (!calmRows.length) { this.restoreCart(drawer); return; }
      drawer.setAttribute('data-calm-cart', '');
      calmRows.forEach(row => {
        row.setAttribute('data-calm-item', '');
        const title = row.querySelector('.cart-item__name');
        if (!title.hasAttribute('data-calm-original-title')) title.dataset.calmOriginalTitle = title.textContent;
        setText(title, this.root.dataset.calmTitle);
      });

      const footer = drawer.querySelector('.drawer__footer');
      const checkout = drawer.querySelector('#CartDrawer-Checkout');
      if (footer && checkout && this.guarantee) {
        let note = footer.querySelector('[data-calm-cart-guarantee]');
        if (!note) {
          note = document.createElement('p');
          note.className = 'calm-cart__guarantee';
          note.setAttribute('data-calm-cart-guarantee', '');
          checkout.insertAdjacentElement('afterend', note);
        }
        setText(note, this.guarantee);
      }

      const discount = drawer.querySelector('.cart-discount');
      if (discount && !discount.closest('[data-calm-discount]') && this.root.dataset.calmDiscountLabel) {
        const details = document.createElement('details');
        details.className = 'calm-cart__discount';
        details.setAttribute('data-calm-discount', '');
        const summary = document.createElement('summary');
        summary.textContent = this.root.dataset.calmDiscountLabel;
        details.append(summary);
        discount.before(details);
        details.append(discount);
      }

      // Offer a native cart-quantity change only for one matching CALM variant in an otherwise empty cart.
      const row = calmRows[0];
      const link = row.querySelector('.cart-item__name');
      const cartVariant = new URL(link.href, window.location.origin).searchParams.get('variant');
      const quantity = row.querySelector('.quantity__input');
      const two = this.inputs.find(input => input.value === '2');
      const price = two && this.labelFor(two)?.querySelector('.quantity-break__price')?.textContent.trim();
      const canUpgrade = rows.length === 1 && quantity && Number(quantity.value) === 1 && !quantity.disabled && cartVariant === this.variant && price && this.root.dataset.calmUpgradeLabel;
      const progress = drawer.querySelector('.cart-progress');
      let upgrade = drawer.querySelector('[data-calm-upgrade]');
      if (canUpgrade && progress) {
        progress.hidden = true;
        progress.setAttribute('data-calm-hidden-progress', '');
        if (!upgrade) {
          upgrade = document.createElement('button');
          upgrade.type = 'button';
          upgrade.className = 'calm-cart__upgrade';
          progress.after(upgrade);
        }
        upgrade.dataset.calmUpgrade = quantity.id;
        const updating = Boolean(drawer.querySelector('.cart__items--disabled'));
        if (upgrade.disabled !== updating) upgrade.disabled = updating;
        setText(upgrade, this.root.dataset.calmUpgradeLabel.replace('[price]', price));
      } else {
        upgrade?.remove();
        if (progress?.hasAttribute('data-calm-hidden-progress')) {
          progress.hidden = false;
          progress.removeAttribute('data-calm-hidden-progress');
        }
      }
      let shipping = drawer.querySelector('[data-calm-cart-shipping]');
      const shippingText = rows.length === 1 && Number(quantity?.value) === 1 ? this.root.dataset.calmShippingText : '';
      if (shippingText && footer) {
        if (!shipping) {
          shipping = document.createElement('p');
          shipping.className = 'calm-cart__shipping';
          shipping.setAttribute('data-calm-cart-shipping','');
          footer.prepend(shipping);
        }
        setText(shipping, shippingText);
      } else shipping?.remove();
    }

    restoreCart(drawer) {
      drawer.removeAttribute('data-calm-cart');
      drawer.querySelectorAll('[data-calm-original-title]').forEach(title => {
        setText(title, title.dataset.calmOriginalTitle);
        title.removeAttribute('data-calm-original-title');
      });
      drawer.querySelectorAll('[data-calm-item]').forEach(row => row.removeAttribute('data-calm-item'));
      drawer.querySelectorAll('[data-calm-cart-guarantee], [data-calm-cart-shipping], [data-calm-upgrade]').forEach(node => node.remove());
      drawer.querySelectorAll('[data-calm-hidden-progress]').forEach(node => { node.hidden = false; node.removeAttribute('data-calm-hidden-progress'); });
      drawer.querySelectorAll('[data-calm-discount]').forEach(details => {
        const discount = details.querySelector('.cart-discount');
        if (discount) details.before(discount);
        details.remove();
      });
    }

    disconnect() {
      this.abort.abort();
      this.observer.disconnect();
      this.cartObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      document.querySelectorAll('[data-calm-repeat-offer]').forEach(section => {
        section.querySelector('[data-calm-repeat-controls]').hidden = true;
        const fallback = section.querySelector('[data-calm-repeat-fallback]');
        fallback.hidden = false;
        fallback.href = '#MainContent';
      });
      const drawer = this.cart?.querySelector('#CartDrawer');
      if (drawer) this.restoreCart(drawer);
    }
  }

  const mount = () => {
    for (const [root, controller] of controllers) {
      if (!root.isConnected) { controller.disconnect(); controllers.delete(root); }
    }
    document.querySelectorAll('[data-calm-pdp]').forEach(root => {
      if (!controllers.has(root)) controllers.set(root, new CalmPdp(root));
    });
    controllers.forEach(controller => controller.schedule());
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount, {once: true});
  else mount();
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, controller] of controllers) {
      if (event.target.contains(root)) { controller.disconnect(); controllers.delete(root); }
    }
  });
})();
